/**
 * Operating-dashboard derivations (Reports Phase B.2, migration 0153).
 *
 * PURE, display-only helpers behind /reports/operating. They derive the
 * founder's richer PDF-dashboard numbers from data ALREADY on the wire — no
 * new endpoint, no server compute: week-over-week deltas, the plan-verdict
 * band, CM/SPICE weekly stats, per-clinician sparkline stats, and the
 * combined cash/AR series. Money stays a STRING on the wire (no-float
 * posture); these helpers Number() only for arithmetic and never round-trip
 * a float back onto the wire.
 */

import type { WeeklyOperatingMetricWire, OperatingClinicianRole } from '@/lib/api';

// ---------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------

export function money(v: string | null): string {
  if (v === null) return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/** Format a computed number as whole-dollar currency. */
export function moneyNum(n: number): string {
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function hours(v: string | null): string {
  if (v === null) return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} hrs`;
}

/** A signed percent, e.g. "+4.9%" / "-2.0%" / "flat". */
export function signedPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '–';
  if (Math.abs(n) < 0.05) return 'flat';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

export function formatWeekEnd(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Month label from a YYYY-MM-DD date OR a YYYY-MM month key. */
export function monthLabel(iso: string): string {
  const anchor = iso.length === 7 ? `${iso}-01` : iso;
  const d = new Date(`${anchor}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// ---------------------------------------------------------------------
// numeric helpers
// ---------------------------------------------------------------------

function num(v: string | null): number | null {
  if (v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mean(xs: number[]): number | null {
  if (xs.length === 0) return null;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1]! + s[mid]!) / 2 : s[mid]!;
}

/** Mean of the last 4 PRESENT values within a chronological series (nulls /
 * gaps excluded); null when no values are present at all. With fewer than 4
 * present values, averages what exists — same posture as the revenue "last 4
 * wks avg". Shared by weeklySeriesStats (revenue) and the per-clinician
 * "4-week avg" column (clinicianAggregates). */
export function last4PresentAvg(values: Array<number | null>): number | null {
  const present = values.filter((v): v is number => v !== null);
  return mean(present.slice(-4));
}

/** Least-squares slope over present (index, value) points. null if < 2. */
function slope(values: Array<number | null>): number | null {
  const pts: Array<[number, number]> = [];
  values.forEach((v, i) => {
    if (v !== null) pts.push([i, v]);
  });
  if (pts.length < 2) return null;
  const n = pts.length;
  const sx = pts.reduce((s, [x]) => s + x, 0);
  const sy = pts.reduce((s, [, y]) => s + y, 0);
  const sxx = pts.reduce((s, [x]) => s + x * x, 0);
  const sxy = pts.reduce((s, [x, y]) => s + x * y, 0);
  const denom = n * sxx - sx * sx;
  if (denom === 0) return null;
  return (n * sxy - sx * sy) / denom;
}

// ---------------------------------------------------------------------
// Month-vs-plan BAND + verdict
// ---------------------------------------------------------------------

export interface MonthBand {
  month: string;
  mtdActual: number;
  monthPlan: number;
  bandLow: number | null;
  bandHigh: number | null;
  calFrac: number;
  workFrac: number;
  hasPlan: boolean;
  /** null when there is no plan on file. */
  verdict: 'ahead' | 'on_plan' | 'behind' | null;
}

/**
 * Month-vs-plan BAND for the in-progress month (STANDING RULE: always a band,
 * never a single-number verdict, no projection). The plan is prorated two
 * ways — calendar days elapsed and working (Mon–Fri) days elapsed — giving a
 * [low, high] band; MTD actual is positioned against it.
 */
export function monthBand(rows: WeeklyOperatingMetricWire[]): MonthBand | null {
  if (rows.length === 0) return null;
  const latest = rows[0]!;
  const d = new Date(`${latest.week_ending}T00:00:00Z`);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();

  const inMonth = rows.filter((r) => {
    const rd = new Date(`${r.week_ending}T00:00:00Z`);
    return rd.getUTCFullYear() === year && rd.getUTCMonth() === month;
  });

  const mtdActual = inMonth.reduce((s, r) => s + (num(r.total_revenue) ?? 0), 0);
  const planRows = inMonth.filter((r) => r.plan_total !== null);
  const hasPlan = planRows.length > 0;
  const monthPlan = planRows.reduce((s, r) => s + (num(r.plan_total) ?? 0), 0);

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  // Prorate to the COVERAGE horizon — the latest posted Sunday — not to
  // wall-clock today. MTD actual only reaches the last posted close, so
  // prorating the plan further (mid-week "today") would compare a shorter
  // actual against a longer plan and read "behind" spuriously. This also
  // keeps the band deterministic for a given dataset (no wall-clock input).
  const dayOfMonth = d.getUTCDate();
  const calFrac = Math.min(1, dayOfMonth / daysInMonth);
  let workElapsed = 0;
  let workTotal = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(Date.UTC(year, month, day)).getUTCDay();
    if (dow >= 1 && dow <= 5) {
      workTotal += 1;
      if (day <= dayOfMonth) workElapsed += 1;
    }
  }
  const workFrac = workTotal === 0 ? 1 : workElapsed / workTotal;

  const lo = Math.min(calFrac, workFrac);
  const hi = Math.max(calFrac, workFrac);
  const bandLow = hasPlan ? monthPlan * lo : null;
  const bandHigh = hasPlan ? monthPlan * hi : null;

  let verdict: MonthBand['verdict'] = null;
  if (hasPlan && bandLow !== null && bandHigh !== null) {
    if (mtdActual > bandHigh) verdict = 'ahead';
    else if (mtdActual < bandLow) verdict = 'behind';
    else verdict = 'on_plan';
  }

  return {
    month: monthLabel(latest.week_ending),
    mtdActual,
    monthPlan,
    bandLow,
    bandHigh,
    calFrac,
    workFrac,
    hasPlan,
    verdict,
  };
}

export function verdictLabel(v: MonthBand['verdict']): string {
  if (v === 'ahead') return 'Ahead of plan';
  if (v === 'behind') return 'Behind plan';
  if (v === 'on_plan') return 'On plan, within band';
  return 'No plan on file';
}

// ---------------------------------------------------------------------
// Week-over-week + trailing-average deltas
// ---------------------------------------------------------------------

export interface WowStats {
  lastWeek: number | null;
  wowPct: number | null; // last week vs the prior stored week
  vs12wkPct: number | null; // last week vs the 12-week average
  cashDelta: number | null; // last cash minus prior-week cash
}

/**
 * The index of the newest COMPLETED week: the first row (rows are newest
 * first) whose status is not 'preliminary', or null when every row is still
 * running. The hero tile and wowStats read from here, so a running week's
 * partial figure never reads as a week-over-week drop or drags the average.
 */
export function latestCompletedIndex(rows: WeeklyOperatingMetricWire[]): number | null {
  const i = rows.findIndex((r) => r.status !== 'preliminary');
  return i === -1 ? null : i;
}

export function wowStats(rows: WeeklyOperatingMetricWire[]): WowStats {
  const lastWeek = rows[0] ? num(rows[0].total_revenue) : null;
  const priorWeek = rows[1] ? num(rows[1].total_revenue) : null;
  const revVals = rows.map((r) => num(r.total_revenue)).filter((v): v is number => v !== null);
  const avg12 = mean(revVals);
  const wowPct = lastWeek !== null && priorWeek !== null && priorWeek !== 0 ? ((lastWeek - priorWeek) / priorWeek) * 100 : null;
  const vs12wkPct = lastWeek !== null && avg12 !== null && avg12 !== 0 ? ((lastWeek - avg12) / avg12) * 100 : null;
  const lastCash = rows[0] ? num(rows[0].cash) : null;
  const priorCash = rows[1] ? num(rows[1].cash) : null;
  const cashDelta = lastCash !== null && priorCash !== null ? lastCash - priorCash : null;
  return { lastWeek, wowPct, vs12wkPct, cashDelta };
}

// ---------------------------------------------------------------------
// CM / SPICE weekly series + stats
// ---------------------------------------------------------------------

export interface WeeklySeriesStats {
  /** chronological (oldest -> newest) values, null where not reported. */
  series: Array<number | null>;
  avg12: number | null;
  last4Avg: number | null;
  prior4Avg: number | null;
  prior4DeltaPct: number | null; // last-4 avg vs prior-4 avg
  trendPerWeek: number | null; // least-squares $/week
}

/** Build the weekly series + stats for one revenue stream (CM or SPICE). */
export function weeklySeriesStats(
  rows: WeeklyOperatingMetricWire[],
  pick: (r: WeeklyOperatingMetricWire) => string | null,
): WeeklySeriesStats {
  // rows are newest-first; chronological for the chart.
  const chrono = [...rows].reverse();
  const series = chrono.map((r) => num(pick(r)));
  const present = series.filter((v): v is number => v !== null);
  const prior4 = present.slice(-8, -4);
  const last4Avg = last4PresentAvg(series);
  const prior4Avg = mean(prior4);
  const prior4DeltaPct =
    last4Avg !== null && prior4Avg !== null && prior4Avg !== 0 ? ((last4Avg - prior4Avg) / prior4Avg) * 100 : null;
  return {
    series,
    avg12: mean(present),
    last4Avg,
    prior4Avg,
    prior4DeltaPct,
    trendPerWeek: slope(series),
  };
}

// ---------------------------------------------------------------------
// Combined cash + AR series
// ---------------------------------------------------------------------

export interface CashArSeries {
  labels: string[]; // chronological week-end labels
  cash: Array<number | null>;
  ar: Array<number | null>;
}

export function cashArSeries(rows: WeeklyOperatingMetricWire[]): CashArSeries {
  const chrono = [...rows].reverse();
  return {
    labels: chrono.map((r) => formatWeekEnd(r.week_ending)),
    cash: chrono.map((r) => num(r.cash)),
    ar: chrono.map((r) => num(r.ar_total)),
  };
}

// ---------------------------------------------------------------------
// Per-clinician hours aggregate (last week + 4-wk avg + flag; founder's
// column rework — the Trend sparkline and the Median DISPLAY column are
// gone, but median stays computed internally to feed the self-band flag)
// ---------------------------------------------------------------------

export type ClinicianFlag = 'high' | 'low' | 'steady';

export interface ClinicianAgg {
  name: string;
  role: OperatingClinicianRole;
  /** chronological hours per week window, null where not reported. */
  series: Array<number | null>;
  /** count of weeks that actually reported hours for this clinician. */
  weeksWithData: number;
  lastWeek: number | null;
  /** mean of the last 4 PRESENT weekly values (gaps excluded); null with 0
   * weeks reported. The "4-week avg" display column. */
  avg4: number | null;
  /** internal only, NOT a display column: feeds the self-band flag
   * fallback (median ±25%) when no roster target is on file. */
  median: number | null;
}

/**
 * Name canonicalization for matching a clinician_hours name to a roster
 * target (NFC + trim + casefold + collapse whitespace). MUST stay in sync
 * with the partner-name normalizer (partnerEngagement.ts) and the server
 * validator's normalizedPartnerKey — same posture, opaque display strings.
 */
export function normalizeName(name: string): string {
  return name.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The pre-comma normalized key: payload clinician names often carry
 * credential suffixes ("Clementine Wheatmock, RN", "Maria Curry, BSN, RN") while
 * staff_roster.full_name is bare (credentials live in a SEPARATE column,
 * 0094). Matching keys on the substring before the FIRST comma bridges the
 * two without maintaining a credential dictionary. */
function preCommaKey(name: string): string {
  return normalizeName(name.split(',')[0] ?? name);
}

/**
 * Build the roster-target lookup for the clinician tables (B.3 fix-first).
 * Two-stage match, exact first:
 *   1. EXACT: normalizeName(payload) === normalizeName(roster).
 *   2. SECOND CHANCE (credential suffixes): compare pre-comma keys on BOTH
 *      sides: payload "Clementine Wheatmock, RN" matches roster
 *      "Clementine Wheatmock", and defensively a suffixed roster name matches a
 *      bare payload name. The second chance applies ONLY when the pre-comma
 *      key resolves to exactly ONE roster person; if two different roster
 *      names collapse to the same pre-comma key, no match (never guess
 *      between people).
 * Returns hours (a finite number) or null (no target on file / ambiguous).
 */
export function buildTargetLookup(
  rosterTargets: ReadonlyArray<{ name: string; target_weekly_hours: string }>,
): (name: string) => number | null {
  const exact = new Map<string, number>();
  // pre-comma key -> the distinct exact roster keys that collapse onto it
  // (value kept per exact key; >1 distinct key = ambiguous, no second chance).
  const byPreComma = new Map<string, Map<string, number>>();
  for (const t of rosterTargets) {
    const n = Number(t.target_weekly_hours);
    if (!Number.isFinite(n)) continue;
    const k = normalizeName(t.name);
    exact.set(k, n);
    const pk = preCommaKey(t.name);
    let bucket = byPreComma.get(pk);
    if (!bucket) {
      bucket = new Map<string, number>();
      byPreComma.set(pk, bucket);
    }
    bucket.set(k, n);
  }
  return (name: string): number | null => {
    const hit = exact.get(normalizeName(name));
    if (hit !== undefined) return hit;
    const bucket = byPreComma.get(preCommaKey(name));
    if (bucket && bucket.size === 1) {
      for (const v of bucket.values()) return v;
    }
    return null;
  };
}

/**
 * Aggregate per-clinician hours across the weekly window from each week's
 * sections.clinician_hours array. The FE derivation: the count of weeks with
 * data, the last full week, the 4-week avg (last 4 present values, gaps
 * excluded), and the median (internal — feeds the self-band flag, not a
 * display column). The ▲high / ▼low / steady FLAG is NOT computed here — it
 * is target-aware and lives in clinicianDisplay (a clinician with a
 * contracted target flags vs that target; without one, vs their own median
 * ±25%). Display-only.
 */
export function clinicianAggregates(rows: WeeklyOperatingMetricWire[]): ClinicianAgg[] {
  const chrono = [...rows].reverse();
  const order: string[] = [];
  const byName = new Map<string, { role: OperatingClinicianRole; series: Array<number | null> }>();

  chrono.forEach((r, weekIdx) => {
    const list = r.sections?.clinician_hours ?? [];
    for (const c of list) {
      let entry = byName.get(c.name);
      if (!entry) {
        entry = { role: c.role, series: new Array<number | null>(chrono.length).fill(null) };
        byName.set(c.name, entry);
        order.push(c.name);
      }
      entry.role = c.role; // most-recent wins (chronological iteration)
      const h = num(c.hours);
      entry.series[weekIdx] = h;
    }
  });

  return order.map((name) => {
    const { role, series } = byName.get(name)!;
    const present = series.filter((v): v is number => v !== null);
    const med = median(present);
    const avg4 = last4PresentAvg(series);
    // Latest full week = the last non-null in the series.
    let lastWeek: number | null = null;
    for (let i = series.length - 1; i >= 0; i--) {
      if (series[i] !== null) {
        lastWeek = series[i]!;
        break;
      }
    }
    return { name, role, series, weeksWithData: present.length, lastWeek, avg4, median: med };
  });
}

/** Minimum weeks of data before the SELF-BAND flag (median ±25%) is trusted.
 * Below this the median equals the last week (or is off one point), so a
 * "steady" verdict would be trivially true — suppress it (render a dash). A
 * TARGET-based flag needs no history (it compares the last week to a fixed
 * contracted number), so this floor does NOT gate it. */
export const MIN_WEEKS_FOR_SELF_FLAG = 3;

export interface ClinicianDisplay {
  /** last full week ÷ contracted target, as a percent; null when no target
   * on file or no reported week. */
  utilizationPct: number | null;
  /** true when utilization sits outside the 60%–110% target band (either
   * direction) — the "give this a look" signal for the register color. */
  utilizationOutOfBand: boolean;
  /** ▲high / ▼low / steady, or null (dash) when it cannot be stated honestly:
   * target-based when a target exists (▼ < 60%, ▲ > 110%, else steady),
   * else the self-band (median ±25%) once >= 3 weeks of data exist. */
  flag: ClinicianFlag | null;
}

/**
 * The target-aware display derivation for one clinician (B.3 founder pass).
 * `target` is the contracted weekly hours matched from staff_roster (null =
 * none on file → self-band fallback).
 */
export function clinicianDisplay(agg: ClinicianAgg, target: number | null): ClinicianDisplay {
  const hasTarget = target !== null && Number.isFinite(target) && target > 0;
  const utilizationPct = hasTarget && agg.lastWeek !== null ? (agg.lastWeek / target) * 100 : null;

  // ONE source of truth for the band: the raw-hours comparisons below decide
  // BOTH the flag and the cell styling. Computing out-of-band separately in
  // percentage space drifts at exact boundaries ((16.5/15)*100 floats to
  // 110.000…01 > 110 while 16.5 > 15*1.1 is false) and the two columns would
  // disagree.
  let targetLow = false;
  let targetHigh = false;

  let flag: ClinicianFlag | null = null;
  if (agg.lastWeek !== null) {
    if (hasTarget) {
      // Target-based: valid off a single reported week.
      targetLow = agg.lastWeek < target * 0.6;
      targetHigh = agg.lastWeek > target * 1.1;
      flag = targetLow ? 'low' : targetHigh ? 'high' : 'steady';
    } else if (agg.weeksWithData >= MIN_WEEKS_FOR_SELF_FLAG && agg.median !== null && agg.median > 0) {
      // Self-band fallback (no target): median ±25%, only once trend exists.
      if (agg.lastWeek > agg.median * 1.25) flag = 'high';
      else if (agg.lastWeek < agg.median * 0.75) flag = 'low';
      else flag = 'steady';
    }
  }
  const utilizationOutOfBand = targetLow || targetHigh;
  return { utilizationPct, utilizationOutOfBand, flag };
}

// ---------------------------------------------------------------------
// Clinician-table row ordering (founder's column rework — last week /
// 4-wk avg / % of target / flag)
// ---------------------------------------------------------------------

export interface ClinicianTableRow {
  agg: ClinicianAgg;
  target: number | null;
  display: ClinicianDisplay;
}

/** Build one row per clinician: the matched roster target + derived display
 * bundled once so a table and its sort share a single source of truth. */
export function buildClinicianRows(
  list: ClinicianAgg[],
  targets: (name: string) => number | null,
): ClinicianTableRow[] {
  return list.map((agg) => {
    const target = targets(agg.name);
    return { agg, target, display: clinicianDisplay(agg, target) };
  });
}

/**
 * CARE MANAGEMENT table ordering: "% of target" descending (highest first).
 * A clinician with NO matched target has no ratio to rank by, so those rows
 * sort to the BOTTOM, after every with-target row — tiebroken among
 * themselves by last-week hours descending.
 */
export function sortCmRows(rows: ClinicianTableRow[]): ClinicianTableRow[] {
  return [...rows].sort((a, b) => {
    const aPct = a.display.utilizationPct;
    const bPct = b.display.utilizationPct;
    if (aPct === null && bPct === null) {
      return (b.agg.lastWeek ?? -Infinity) - (a.agg.lastWeek ?? -Infinity);
    }
    if (aPct === null) return 1;
    if (bPct === null) return -1;
    return bPct - aPct;
  });
}

/**
 * SPICE table ordering: SPICE clinicians carry no contracted target (no
 * "% of target" column), so the sort key is the 4-week avg descending
 * instead, tiebroken by last-week hours descending.
 */
export function sortSpiceRows(rows: ClinicianTableRow[]): ClinicianTableRow[] {
  return [...rows].sort((a, b) => {
    const diff = (b.agg.avg4 ?? -Infinity) - (a.agg.avg4 ?? -Infinity);
    if (diff !== 0) return diff;
    return (b.agg.lastWeek ?? -Infinity) - (a.agg.lastWeek ?? -Infinity);
  });
}

// ---------------------------------------------------------------------
// Monthly aggregates from the weekly rows (the existing table's basis).
// sections.monthly, when present, is the richer source for the line chart.
// ---------------------------------------------------------------------

export interface MonthAgg {
  key: string; // YYYY-MM
  label: string;
  actual: number;
  plan: number | null;
  priorYear: number | null;
}

export function monthlyAggregates(
  rows: WeeklyOperatingMetricWire[],
  monthlySection?: Array<{ month: string; actual?: string | null; plan?: string | null; prior_year?: string | null }> | null,
): MonthAgg[] {
  const byMonth = new Map<string, { actual: number; plan: number | null; label: string }>();
  for (const r of rows) {
    const d = new Date(`${r.week_ending}T00:00:00Z`);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const cur = byMonth.get(key) ?? { actual: 0, plan: null, label: monthLabel(r.week_ending) };
    if (r.total_revenue !== null) cur.actual += num(r.total_revenue) ?? 0;
    if (r.plan_total !== null) cur.plan = (cur.plan ?? 0) + (num(r.plan_total) ?? 0);
    byMonth.set(key, cur);
  }
  // sections.monthly, when present, backfills what the 12-week window cannot
  // know: prior-year actuals, and plan for months with no posted weeks. The
  // weekly-derived actual stays authoritative for months that HAVE weeks.
  const section = new Map<string, { plan: number | null; priorYear: number | null }>();
  for (const m of monthlySection ?? []) {
    if (typeof m?.month === 'string' && /^\d{4}-\d{2}$/.test(m.month)) {
      section.set(m.month, { plan: num(m.plan ?? null), priorYear: num(m.prior_year ?? null) });
    }
  }
  const out: MonthAgg[] = [];
  for (const [key, v] of byMonth.entries()) {
    const [y, m] = key.split('-').map(Number);
    const priorKey = `${(y ?? 0) - 1}-${String(m).padStart(2, '0')}`;
    const prior = byMonth.get(priorKey);
    const sec = section.get(key);
    out.push({
      key,
      label: v.label,
      actual: v.actual,
      plan: v.plan ?? sec?.plan ?? null,
      priorYear: prior?.actual ?? sec?.priorYear ?? null,
    });
  }
  return out.sort((a, b) => (a.key < b.key ? 1 : -1)).slice(0, 6);
}
